/**
 * Accessible names for the icon-only controls repeated on every card.
 *
 * On /sites nearly every button was unnamed: the favorite and itinerary
 * buttons only got an aria-label while offline, and then the label
 * ("Unavailable offline") replaced what the button does. These helpers give
 * every state a name that says what the control acts on.
 */

type Item = { name?: string; fields?: Record<string, unknown> } | null | undefined;

/** Display name for a site, event, tour or custom itinerary record. */
export function itemDisplayName(item: Item): string {
    const fields = item?.fields ?? {};
    for (const key of ['Site', 'Program Title', 'Name']) {
        const value = fields[key];
        if (typeof value === 'string' && value.trim()) return value.trim();
    }
    return typeof item?.name === 'string' ? item.name.trim() : '';
}

/** "Favorite <name>" or "Add <name> to itinerary", depending on the list. */
export function userActionLabel(list: string, name: string): string {
    if (list === 'ohcItineraries') return name ? `Add ${name} to itinerary` : 'Add to itinerary';
    return name ? `Favorite ${name}` : 'Favorite';
}

/**
 * Google Maps search query for a site. Bare street addresses ("111 W Wacker")
 * resolve to the wrong city surprisingly often, so the city and state are
 * appended — from the record when it has them, otherwise Chicago, IL.
 */
export function directionsQuery(fields: Record<string, unknown> | null | undefined): string {
    const address = typeof fields?.['Site Address'] === 'string' ? (fields['Site Address'] as string).trim() : '';
    if (!address) return '';
    const city = typeof fields?.City === 'string' && fields.City.trim() ? fields.City.trim() : 'Chicago';
    const state = typeof fields?.State === 'string' && fields.State.trim() ? fields.State.trim() : 'IL';
    if (address.toLowerCase().includes(city.toLowerCase())) return address;
    return `${address}, ${city}, ${state}`;
}
